'use client';

import type { FC, ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '../utils/cn';
import { useScreenLayout } from './useScreenLayout';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { DesktopSiteFooter } from '../components/desktop/DesktopSiteFooter';
import { useDesktopContentWidth } from '../contexts/DesktopContentWidthContext';

interface ScreenContentProps {
  children: ReactNode;
  className?: string;
  resetScrollOnMount?: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
  scrollKey?: string;
  hideLegalSection?: boolean;
  respectSafeArea?: boolean;
  topAccessory?: ReactNode;
  symmetricSafeArea?: 'horizontal' | 'all';
  landscapeCutout?: 'left' | 'right';
}

export const ScreenContent: FC<ScreenContentProps> = ({
  children,
  className,
  resetScrollOnMount = true,
  scrollRef: externalScrollRef,
  scrollKey,
  hideLegalSection = false,
  respectSafeArea = false,
  symmetricSafeArea,
  landscapeCutout,
  topAccessory,
}) => {
  const internalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = externalScrollRef || internalScrollRef;
  const { disableSafeArea } = useScreenLayout();
  const isDesktop = useIsDesktop();
  const desktopContentWidth = useDesktopContentWidth();
  const hasDesktopFooter = isDesktop && !hideLegalSection;

  useScrollRestoration({
    scrollKey: scrollKey || '',
    scrollRef,
    enabled: !!scrollKey,
  });

  useEffect(() => {
    if (!scrollKey && resetScrollOnMount && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scrollKey, resetScrollOnMount, scrollRef]);

  return (
    <div
      ref={scrollRef}
      data-screen-scroll
      className={cn(
        'absolute top-0 start-0 end-0 bottom-0 overflow-y-auto overflow-x-hidden [container-type:size] [overflow-anchor:auto] [-webkit-overflow-scrolling:touch] pt-[max(var(--screen-fixed-top-height,0px),calc(var(--tg-viewport-content-safe-area-inset-top,0px)+var(--tg-viewport-safe-area-inset-top,0px)))] pb-[max(calc(var(--screen-fixed-bottom-height,0px)+var(--screen-fixed-bottom-lift,0px)+var(--screen-fixed-bottom-gap,0px)),var(--screen-tabbar-height,0px),calc(var(--tg-viewport-content-safe-area-inset-bottom,0px)+var(--tg-viewport-safe-area-inset-bottom,0px)))]',
        respectSafeArea && !symmetricSafeArea && 'ml-[calc(var(--tg-viewport-safe-area-inset-left,0px)+var(--tg-viewport-content-safe-area-inset-left,0px))] mr-[calc(var(--tg-viewport-safe-area-inset-right,0px)+var(--tg-viewport-content-safe-area-inset-right,0px))]',
        symmetricSafeArea && 'mx-[max(calc(var(--tg-viewport-safe-area-inset-left,0px)+var(--tg-viewport-content-safe-area-inset-left,0px)),calc(var(--tg-viewport-safe-area-inset-right,0px)+var(--tg-viewport-content-safe-area-inset-right,0px)))]',
        symmetricSafeArea === 'all' && '!pt-[max(calc(var(--tg-viewport-safe-area-inset-top,0px)+var(--tg-viewport-content-safe-area-inset-top,0px)),calc(var(--tg-viewport-safe-area-inset-bottom,0px)+var(--tg-viewport-content-safe-area-inset-bottom,0px)))] !pb-[max(calc(var(--tg-viewport-safe-area-inset-top,0px)+var(--tg-viewport-content-safe-area-inset-top,0px)),calc(var(--tg-viewport-safe-area-inset-bottom,0px)+var(--tg-viewport-content-safe-area-inset-bottom,0px)))]',
        landscapeCutout && '!pt-0 !pb-[min(16px,calc(var(--tg-viewport-safe-area-inset-bottom,0px)+var(--tg-viewport-content-safe-area-inset-bottom,0px)))]',
        landscapeCutout && topAccessory && '!pt-9',
        landscapeCutout === 'left' && !symmetricSafeArea && 'ml-[calc(var(--tg-viewport-safe-area-inset-left,0px)+var(--tg-viewport-content-safe-area-inset-left,0px))] mr-0',
        landscapeCutout === 'right' && !symmetricSafeArea && 'mr-[calc(var(--tg-viewport-safe-area-inset-right,0px)+var(--tg-viewport-content-safe-area-inset-right,0px))] ml-0',
        hasDesktopFooter && 'flex flex-col',
        desktopContentWidth === 'column' && !hasDesktopFooter && 'mx-auto max-w-4xl',
        !isDesktop && 'bg-black',
        disableSafeArea && !respectSafeArea && !landscapeCutout && '!pt-[var(--screen-fixed-top-height,0px)] !pb-[max(calc(var(--screen-fixed-bottom-height,0px)+var(--screen-fixed-bottom-lift,0px)+var(--screen-fixed-bottom-gap,0px)),var(--screen-tabbar-height,0px))]',
        className
      )}
    >
      <div
        data-screen-body
        className={cn(
          !hasDesktopFooter && 'contents',
          hasDesktopFooter && 'relative mx-auto w-full',
          hasDesktopFooter && desktopContentWidth === 'column' && 'max-w-4xl'
        )}
      >
        {topAccessory && <div data-screen-top-accessory style={landscapeCutout ? {
          top: 0, left: '50vw',
          right: 'auto', width: 'calc(100vw - 200px)', transform: 'translateX(-50%)',
        } : undefined} className={cn('absolute inset-x-12 top-[var(--tg-viewport-safe-area-inset-top,0px)] flex h-[max(24px,var(--tg-viewport-content-safe-area-inset-top,0px))] items-center justify-center', landscapeCutout && 'fixed! h-9! z-10')}>{topAccessory}</div>}
        {children}
      </div>
      {hasDesktopFooter && <DesktopSiteFooter />}
    </div>
  );
};
